import { zodResolver } from '@hookform/resolvers/zod';
import * as Tooltip from '@radix-ui/react-tooltip';
import { AlertTriangle, CheckCircle2, CircleHelp, Minus, ShieldAlert, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  AddMarketHealthWatchlistItemSchema,
  type AddMarketHealthWatchlistItemInput,
  type MarketHealthWatchlistItemDto,
  type MarketInternalMetricDto,
  type MarketOutlookStateDto,
} from 'shared';

import { Button } from '../components/ui/Button';
import { CollapsiblePanel } from '../components/ui/CollapsiblePanel';
import { Input } from '../components/ui/Input';
import { Sparkline } from '../components/ui/Sparkline';
import { indicatorHelp, type IndicatorHelpKey } from '../features/marketHealth/indicatorHelp';
import {
  useAddMarketHealthWatchlistItem,
  useRemoveMarketHealthWatchlistItem,
} from '../features/marketHealth/mutations';
import { useMarketHealth } from '../features/marketHealth/queries';
import { cn } from '../lib/utils';

type Tone = 'success' | 'warn' | 'danger' | 'muted';

const toneClasses: Record<Tone, string> = {
  success: 'border-success/30 bg-success/10 text-success',
  warn: 'border-warn/30 bg-warn/10 text-warn',
  danger: 'border-danger/30 bg-danger/10 text-danger',
  muted: 'border-border bg-bg-muted text-text-muted',
};

function formatPrice(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatOutlook(state: MarketOutlookStateDto): string {
  if (state === 'CONFIRMED_UPTREND') return 'Confirmed uptrend';
  if (state === 'UPTREND_UNDER_PRESSURE') return 'Uptrend under pressure';
  return 'Market in correction';
}

function ordinal(value: number): string {
  const remainder100 = value % 100;
  if (remainder100 >= 11 && remainder100 <= 13) return `${value}th`;
  if (value % 10 === 1) return `${value}st`;
  if (value % 10 === 2) return `${value}nd`;
  if (value % 10 === 3) return `${value}rd`;
  return `${value}th`;
}

function formatOutlookComputationStatus(
  status: 'BACKFILL_REQUIRED' | 'BACKFILLED' | 'LIVE' | 'STALE',
  asOfDate: string,
): string {
  if (status === 'BACKFILL_REQUIRED') return `Outlook backfill required · computed ${asOfDate}`;
  if (status === 'BACKFILLED') return `Backfilled through ${asOfDate}`;
  if (status === 'STALE') return `Outlook stale · last computed ${asOfDate}`;
  return `Live outlook · as of ${asOfDate}`;
}

function formatRelative(value: number | null): string {
  if (value === null) return '—';
  if (value === 0) return '● 0.0%';
  return `${value > 0 ? '▲ +' : '▼ '}${value.toFixed(1)}%`;
}

function relativeTone(value: number | null): string {
  if (value === null || value === 0) return 'text-text-muted';
  return value > 0 ? 'text-success' : 'text-danger';
}

function StatusBadge({ label, tone }: { label: string; tone: Tone }) {
  const Icon =
    tone === 'success'
      ? CheckCircle2
      : tone === 'danger'
        ? ShieldAlert
        : tone === 'warn'
          ? AlertTriangle
          : Minus;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
        toneClasses[tone],
      )}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

function IndicatorHelp({
  children,
  indicator,
}: {
  children?: string;
  indicator: IndicatorHelpKey;
}) {
  const copy = indicatorHelp[indicator];
  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            aria-label={`Help: ${copy.label}`}
            className="inline-flex items-center gap-1 border-b border-dotted border-text-subtle"
          >
            {children ?? copy.label}
            <CircleHelp className="h-3 w-3 text-text-subtle" aria-hidden="true" />
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="z-50 max-w-64 rounded border border-border bg-surface-raised px-3 py-2 text-xs text-text shadow-lg"
            sideOffset={5}
          >
            {copy.description}
            <Tooltip.Arrow className="fill-surface-raised" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

function RegimeCell({ children, label }: { children: ReactNode; label: ReactNode }) {
  return (
    <div className="flex min-h-36 flex-col border-border p-4 sm:border-l sm:first:border-l-0">
      <div className="text-xs font-medium uppercase tracking-wider text-text-muted">{label}</div>
      <div className="mt-3 flex flex-1 flex-col">{children}</div>
    </div>
  );
}

function InternalMetric({
  badge,
  datePrefix = 'As of',
  formatChange = (value) => `${value > 0 ? '+' : ''}${value.toFixed(2)}`,
  format = (value) => value.toFixed(2),
  label,
  metric,
}: {
  badge?: ReactNode;
  datePrefix?: string;
  formatChange?: (value: number) => string;
  format?: (value: number) => string;
  label: ReactNode;
  metric: MarketInternalMetricDto;
}) {
  return (
    <div className="rounded border border-border bg-bg-muted px-3 py-2.5">
      <div className="text-xs text-text-muted">{label}</div>
      {metric.status !== 'NOT_AVAILABLE' && metric.value !== null ? (
        <div className="mt-1 flex items-baseline justify-between gap-2 font-mono tabular-nums">
          <span className="text-lg font-semibold">{format(metric.value)}</span>
          {metric.change !== null ? (
            <span className={relativeTone(metric.change)}>{formatChange(metric.change)}</span>
          ) : null}
        </div>
      ) : (
        <div className="mt-1 text-sm font-medium text-text-subtle">Awaiting first run</div>
      )}
      {badge ? <div className="mt-1.5">{badge}</div> : null}
      <div
        className={cn(
          'mt-1 truncate text-[10px]',
          metric.status === 'STALE' ? 'font-medium text-warn' : 'text-text-subtle',
        )}
      >
        {metric.status === 'STALE' && metric.asOfDate
          ? `Stale — last updated ${metric.asOfDate}`
          : metric.asOfDate
            ? `${datePrefix} ${metric.asOfDate}${metric.source ? ` · ${metric.source}` : ''}`
            : 'Nightly EOD data'}
      </div>
    </div>
  );
}

function CreditConditionBadge({
  kind,
  metric,
}: {
  kind: 'financialConditions' | 'creditRatio';
  metric: MarketInternalMetricDto;
}) {
  const signal = kind === 'creditRatio' ? metric.change : metric.value;
  if (signal === null || metric.status === 'NOT_AVAILABLE') return null;
  if (signal === 0) return <StatusBadge label="At average" tone="muted" />;
  if (kind === 'financialConditions') {
    return (
      <StatusBadge
        label={signal > 0 ? 'Tighter' : 'Looser'}
        tone={signal > 0 ? 'danger' : 'success'}
      />
    );
  }
  return (
    <StatusBadge
      label={signal > 0 ? 'Improving' : 'Stress building'}
      tone={signal > 0 ? 'success' : 'warn'}
    />
  );
}

function atrTone(distance: number): Tone {
  const magnitude = Math.abs(distance);
  if (magnitude > 2.5) return 'danger';
  if (magnitude >= 1) return 'warn';
  return 'muted';
}

function formatAtrDistance(distance: number): string {
  const direction = distance >= 0 ? 'above' : 'below';
  return `${distance > 0 ? '+' : ''}${distance.toFixed(1)} ATR ${direction}`;
}

function WatchlistMobileRow({
  item,
  onRemove,
  removing,
}: {
  item: MarketHealthWatchlistItemDto;
  onRemove: (symbol: string) => void;
  removing: boolean;
}) {
  return (
    <article className="rounded border border-border bg-bg-muted p-3 md:hidden">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div>
            <div className="font-semibold">{item.symbol}</div>
            <div className="font-mono text-sm tabular-nums text-text-muted">
              {item.trend ? formatPrice(item.trend.price) : 'No history'}
            </div>
          </div>
          {item.trend ? (
            <Sparkline
              className="ml-auto w-28"
              data={item.trend.history}
              label={`${item.symbol} last 30 sessions`}
            />
          ) : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`Remove ${item.symbol} from watchlist`}
          disabled={removing}
          onClick={() => onRemove(item.symbol)}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
      {item.trend ? (
        <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-border pt-3 text-right font-mono text-xs tabular-nums sm:grid-cols-6">
          <div>
            <dt className="text-text-muted">20DMA</dt>
            <dd className="mt-1">{formatPrice(item.trend.sma20)}</dd>
          </div>
          <div>
            <dt className="text-text-muted">50DMA</dt>
            <dd className="mt-1">{formatPrice(item.trend.sma50)}</dd>
          </div>
          <div>
            <dt className="text-text-muted">ATR(50)</dt>
            <dd
              className={cn(
                'mt-1',
                atrTone(item.trend.atrDistanceFromSma50) === 'danger'
                  ? 'text-danger'
                  : atrTone(item.trend.atrDistanceFromSma50) === 'warn'
                    ? 'text-warn'
                    : 'text-text-muted',
              )}
            >
              {item.trend.atrDistanceFromSma50 > 0 ? '+' : ''}
              {item.trend.atrDistanceFromSma50.toFixed(1)}
            </dd>
          </div>
          {(
            [
              { label: '5d', value: item.relativePerformancePct.days5 },
              { label: '10d', value: item.relativePerformancePct.days10 },
              { label: '20d', value: item.relativePerformancePct.days20 },
            ] satisfies Array<{ label: string; value: number | null }>
          ).map(({ label, value }) => (
            <div key={String(label)}>
              <dt className="text-text-muted">{label}</dt>
              <dd className={cn('mt-1', relativeTone(value))}>{formatRelative(value)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </article>
  );
}

export function MarketHealth() {
  const marketHealth = useMarketHealth();
  const addItem = useAddMarketHealthWatchlistItem();
  const removeItem = useRemoveMarketHealthWatchlistItem();
  const [mutationError, setMutationError] = useState<string | null>(null);
  const form = useForm<AddMarketHealthWatchlistItemInput>({
    resolver: zodResolver(AddMarketHealthWatchlistItemSchema),
    defaultValues: { symbol: '' },
  });

  const submit = form.handleSubmit((input) => {
    setMutationError(null);
    addItem.mutate(input, {
      onSuccess: () => form.reset(),
      onError: () =>
        setMutationError(`Could not add ${input.symbol}. Check the symbol and try again.`),
    });
  });

  const remove = (symbol: string) => {
    setMutationError(null);
    removeItem.mutate(symbol, {
      onError: () => setMutationError(`Could not remove ${symbol}.`),
    });
  };

  if (marketHealth.isLoading) {
    return <main className="p-6 text-text-muted">Loading market health…</main>;
  }
  if (marketHealth.isError || !marketHealth.data) {
    return (
      <main className="p-6 text-danger">
        Market-health data is unavailable. Confirm the daily-bar job has completed.
      </main>
    );
  }

  const data = marketHealth.data;
  const strongTrend =
    data.trend.priceVsSma20 === 'ABOVE' &&
    data.trend.priceVsSma50 === 'ABOVE' &&
    data.trend.sma50Slope === 'RISING';
  const trendTone: Tone =
    data.trend.priceVsSma50 === 'BELOW' ? 'danger' : strongTrend ? 'success' : 'warn';
  const distributionTone: Tone = data.distribution.defensiveMode
    ? 'danger'
    : data.distribution.count >= 3
      ? 'warn'
      : 'success';
  const outlookTone: Tone =
    data.marketOutlook.state === 'CONFIRMED_UPTREND'
      ? 'success'
      : data.marketOutlook.state === 'UPTREND_UNDER_PRESSURE'
        ? 'warn'
        : 'danger';

  return (
    <main className="min-h-screen bg-bg text-text">
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-5 sm:px-6">
        <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Market Health</h1>
            <p className="mt-1 text-sm text-text-muted">
              Regime first. Internals and relative strength on demand.
            </p>
          </div>
          <div className="text-xs text-text-muted">
            QQQ close · {data.trend.asOfDate} · Informational only
          </div>
        </header>

        <section
          className="overflow-hidden rounded-lg border border-border bg-surface"
          aria-labelledby="market-regime-heading"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 id="market-regime-heading" className="text-sm font-semibold">
              Market Regime
            </h2>
            <StatusBadge label={formatOutlook(data.marketOutlook.state)} tone={outlookTone} />
          </div>
          <div className="grid divide-y divide-border sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4">
            <RegimeCell label={<IndicatorHelp indicator="trend" />}>
              <div className="flex items-end gap-3">
                <div className="font-mono text-2xl font-semibold tabular-nums">
                  {formatPrice(data.trend.price)}
                </div>
                <Sparkline
                  className="mb-0.5 flex-1"
                  data={data.trend.history}
                  label="QQQ last 30 sessions"
                />
              </div>
              <div className="mt-auto pt-3">
                <StatusBadge
                  label={strongTrend ? 'Uptrend' : 'Trend under pressure'}
                  tone={trendTone}
                />
              </div>
            </RegimeCell>

            <RegimeCell label={<IndicatorHelp indicator="movingAverages" />}>
              <div className="grid grid-cols-2 gap-4 font-mono tabular-nums">
                <div>
                  <div className="text-xs text-text-muted">20DMA</div>
                  <div className="mt-1 font-semibold">{formatPrice(data.trend.sma20)}</div>
                </div>
                <div>
                  <div className="text-xs text-text-muted">50DMA</div>
                  <div className="mt-1 font-semibold">{formatPrice(data.trend.sma50)}</div>
                </div>
              </div>
              <div className="mt-auto pt-3">
                <div className="mb-2 text-xs text-text-muted">
                  <IndicatorHelp indicator="atrDistance">
                    {formatAtrDistance(data.trend.atrDistanceFromSma50)}
                  </IndicatorHelp>
                </div>
                <StatusBadge
                  label={
                    Math.abs(data.trend.atrDistanceFromSma50) > 2.5
                      ? 'Stretched'
                      : Math.abs(data.trend.atrDistanceFromSma50) >= 1
                        ? 'Extended'
                        : `50DMA ${
                            data.trend.sma50Slope === 'RISING' ? 'rising' : 'flat / falling'
                          }`
                  }
                  tone={atrTone(data.trend.atrDistanceFromSma50)}
                />
              </div>
            </RegimeCell>

            <RegimeCell label={<IndicatorHelp indicator="distributionDays" />}>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-2xl font-semibold tabular-nums">
                  {data.distribution.count}
                </span>
                <span className="text-xs text-text-muted">in 25 sessions</span>
              </div>
              <div className="mt-auto flex flex-wrap items-center gap-2 pt-3">
                <StatusBadge
                  label={
                    data.distribution.defensiveMode
                      ? 'Pressure threshold reached'
                      : 'Below pressure threshold'
                  }
                  tone={distributionTone}
                />
                {data.distribution.daysSinceLastDistribution !== null &&
                data.distribution.daysSinceLastDistribution <= 1 ? (
                  <StatusBadge
                    label={`${data.distribution.daysSinceLastDistribution}d · Fresh`}
                    tone="warn"
                  />
                ) : (
                  <span className="text-xs text-text-muted">
                    {data.distribution.daysSinceLastDistribution === null
                      ? 'No recent day'
                      : `${data.distribution.daysSinceLastDistribution}d since last`}
                  </span>
                )}
              </div>
              {data.distribution.postFtdStress ? (
                <p className="mt-2 text-xs font-medium text-warn">
                  Distribution day {data.distribution.postFtdStress.sessionsAfterFtd}d after FTD —
                  rally under early stress.
                </p>
              ) : null}
            </RegimeCell>

            <RegimeCell label={<IndicatorHelp indicator="followThrough" />}>
              <div className="text-base font-semibold">
                {data.marketOutlook.state === 'CONFIRMED_UPTREND'
                  ? 'Follow-through confirmed'
                  : data.marketOutlook.state === 'UPTREND_UNDER_PRESSURE'
                    ? 'Follow-through confirmed'
                    : data.marketOutlook.rallyAttemptDay !== null
                      ? `Rally attempt · Day ${data.marketOutlook.rallyAttemptDay}`
                      : 'In correction — awaiting rally attempt'}
              </div>
              <div className="mt-auto flex flex-wrap items-center gap-2 pt-3">
                <StatusBadge
                  label={
                    data.marketOutlook.state === 'CONFIRMED_UPTREND'
                      ? 'Confirmed'
                      : data.marketOutlook.state === 'UPTREND_UNDER_PRESSURE'
                        ? `Under pressure — ${ordinal(data.distribution.count)} distribution day`
                        : data.marketOutlook.rallyAttemptDay !== null
                          ? 'Watching'
                          : 'Correction'
                  }
                  tone={outlookTone}
                />
                {data.marketOutlook.daysSinceFtd !== null ? (
                  <span className="text-xs text-text-muted">
                    {data.marketOutlook.daysSinceFtd}d since FTD
                  </span>
                ) : data.marketOutlook.rallyAttemptLow !== null ? (
                  <span className="text-xs text-text-muted">
                    Low {formatPrice(data.marketOutlook.rallyAttemptLow)} · started{' '}
                    {data.marketOutlook.rallyAttemptStartDate}
                  </span>
                ) : null}
              </div>
              <p
                className="mt-2 text-xs text-text-muted"
                title={data.marketOutlook.outlookChangeReason}
              >
                {data.marketOutlook.outlookChangeReason}
              </p>
              <p
                className={cn(
                  'mt-1 text-[11px]',
                  data.marketOutlook.computationStatus === 'BACKFILL_REQUIRED'
                    ? 'text-danger'
                    : data.marketOutlook.computationStatus === 'STALE'
                      ? 'text-warn'
                      : 'text-text-muted',
                )}
              >
                {formatOutlookComputationStatus(
                  data.marketOutlook.computationStatus,
                  data.marketOutlook.asOfDate,
                )}
              </p>
            </RegimeCell>
          </div>
        </section>

        <CollapsiblePanel
          title="Market Internals"
          description="Breadth, volatility, participation, and sector rotation"
        >
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
            <InternalMetric
              label={<IndicatorHelp indicator="stocksAbove50Day" />}
              metric={data.internals.stocksAbove50DayPct}
              format={(value) => `${value.toFixed(1)}%`}
            />
            <InternalMetric
              label={<IndicatorHelp indicator="advanceDecline" />}
              metric={data.internals.advanceDeclineLine}
            />
            <InternalMetric
              label={<IndicatorHelp indicator="vix" />}
              metric={data.internals.volatility}
            />
            <InternalMetric
              label={<IndicatorHelp indicator="newHighs" />}
              metric={data.internals.newHighs}
            />
            <InternalMetric
              label={<IndicatorHelp indicator="newLows" />}
              metric={data.internals.newLows}
            />
            <InternalMetric
              label={<IndicatorHelp indicator="putCall" />}
              metric={data.internals.putCallRatio}
            />
          </div>
          <div className="mt-3 rounded border border-border bg-surface px-3 py-3">
            <div className="text-xs font-medium uppercase tracking-wider text-text-muted">
              Credit
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <InternalMetric
                label={<IndicatorHelp indicator="nfciCredit" />}
                metric={data.internals.nfciCredit}
                datePrefix="Week of"
                badge={
                  <CreditConditionBadge
                    kind="financialConditions"
                    metric={data.internals.nfciCredit}
                  />
                }
              />
              <InternalMetric
                label={<IndicatorHelp indicator="nfci" />}
                metric={data.internals.nfci}
                datePrefix="Week of"
                badge={
                  <CreditConditionBadge kind="financialConditions" metric={data.internals.nfci} />
                }
              />
              <InternalMetric
                label={<IndicatorHelp indicator="hygLqd" />}
                metric={data.internals.hygLqdRatio}
                format={(value) => value.toFixed(4)}
                formatChange={(value) => `${value > 0 ? '+' : ''}${value.toFixed(2)}% · 5d`}
                badge={
                  <CreditConditionBadge kind="creditRatio" metric={data.internals.hygLqdRatio} />
                }
              />
            </div>
          </div>
          <div className="mt-3 rounded border border-border bg-bg-muted px-3 py-2.5">
            <div className="text-xs font-medium uppercase tracking-wider text-text-muted">
              <IndicatorHelp indicator="sectorRotation" />
            </div>
            {data.internals.sectorRotation.status === 'READY' ? (
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-text-muted">Leading</span>
                  {data.internals.sectorRotation.leaders.map((sector) => (
                    <StatusBadge
                      key={sector.symbol}
                      label={`${sector.symbol} ${formatRelative(sector.relativePerformancePct)}`}
                      tone="success"
                    />
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-text-muted">Lagging</span>
                  {data.internals.sectorRotation.laggards.map((sector) => (
                    <StatusBadge
                      key={sector.symbol}
                      label={`${sector.symbol} ${formatRelative(sector.relativePerformancePct)}`}
                      tone="danger"
                    />
                  ))}
                </div>
              </div>
            ) : (
              <p className="mt-1 text-sm text-text-subtle">
                Sector-series feed not connected. No estimates are substituted.
              </p>
            )}
          </div>
        </CollapsiblePanel>

        <CollapsiblePanel
          defaultExpanded
          title={`Relative Strength Watchlist · ${data.watchlist.length}`}
          description="Compact trend and performance view versus QQQ"
        >
          <form className="flex flex-col gap-2 sm:flex-row" onSubmit={submit}>
            <div className="flex-1">
              <Input
                aria-label="Watchlist symbol"
                placeholder="Add symbol, e.g. NVDA"
                autoCapitalize="characters"
                {...form.register('symbol')}
              />
              {form.formState.errors.symbol ? (
                <p className="mt-1 text-sm text-danger">{form.formState.errors.symbol.message}</p>
              ) : null}
            </div>
            <Button type="submit" disabled={addItem.isPending}>
              {addItem.isPending ? 'Loading history…' : 'Add symbol'}
            </Button>
          </form>
          {mutationError ? <p className="mt-2 text-sm text-danger">{mutationError}</p> : null}

          {data.watchlist.length > 0 ? (
            <div className="mt-3">
              <div className="space-y-2 md:hidden">
                {data.watchlist.map((item) => (
                  <WatchlistMobileRow
                    key={item.id}
                    item={item}
                    onRemove={remove}
                    removing={removeItem.isPending}
                  />
                ))}
              </div>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[780px] border-collapse text-sm">
                  <thead>
                    <tr className="h-8 border-y border-border text-right text-[11px] uppercase tracking-wide text-text-muted">
                      <th className="px-2 text-left font-medium">Symbol / 30 sessions</th>
                      <th className="px-2 font-medium">Price</th>
                      <th className="px-2 font-medium">20DMA</th>
                      <th className="px-2 font-medium">50DMA</th>
                      <th className="px-2 font-medium">
                        <IndicatorHelp indicator="atrDistance">ATR(50)</IndicatorHelp>
                      </th>
                      <th className="px-2 font-medium">5d vs QQQ</th>
                      <th className="px-2 font-medium">10d vs QQQ</th>
                      <th className="px-2 font-medium">20d vs QQQ</th>
                      <th className="w-10" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.watchlist.map((item) => (
                      <tr key={item.id} className="h-12 border-b border-border/70">
                        <td className="px-2">
                          <div className="flex items-center gap-3">
                            <span className="w-14 font-semibold">{item.symbol}</span>
                            {item.trend ? (
                              <Sparkline
                                className="w-24"
                                data={item.trend.history}
                                label={`${item.symbol} last 30 sessions`}
                              />
                            ) : (
                              <span className="text-xs text-warn">Insufficient history</span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 text-right font-mono tabular-nums">
                          {item.trend ? formatPrice(item.trend.price) : '—'}
                        </td>
                        <td className="px-2 text-right font-mono tabular-nums">
                          {item.trend ? formatPrice(item.trend.sma20) : '—'}
                        </td>
                        <td className="px-2 text-right font-mono tabular-nums">
                          {item.trend ? formatPrice(item.trend.sma50) : '—'}
                        </td>
                        <td
                          className={cn(
                            'px-2 text-right font-mono tabular-nums',
                            item.trend && atrTone(item.trend.atrDistanceFromSma50) === 'danger'
                              ? 'text-danger'
                              : item.trend && atrTone(item.trend.atrDistanceFromSma50) === 'warn'
                                ? 'text-warn'
                                : 'text-text-muted',
                          )}
                        >
                          {item.trend
                            ? `${item.trend.atrDistanceFromSma50 > 0 ? '+' : ''}${item.trend.atrDistanceFromSma50.toFixed(1)}`
                            : '—'}
                        </td>
                        {[
                          item.relativePerformancePct.days5,
                          item.relativePerformancePct.days10,
                          item.relativePerformancePct.days20,
                        ].map((value, index) => (
                          <td
                            key={index}
                            className={cn(
                              'px-2 text-right font-mono tabular-nums',
                              relativeTone(value),
                            )}
                          >
                            {formatRelative(value)}
                          </td>
                        ))}
                        <td className="px-1 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label={`Remove ${item.symbol} from watchlist`}
                            disabled={removeItem.isPending}
                            onClick={() => remove(item.symbol)}
                          >
                            <X className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="mt-3 text-sm text-text-muted">
              No symbols yet. Add one to load its daily history.
            </p>
          )}
        </CollapsiblePanel>
      </div>
    </main>
  );
}
